import React, { useState, useEffect } from 'react';
import { FaUserPlus } from 'react-icons/fa';
import Swal from 'sweetalert2';
import SummaryApi from '../common';
import { useNavigate } from 'react-router-dom';

const StudentCreateAdmin = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile_number: '',
    address: '',
    city: '',
  });

  // Loader state
  const [loading, setLoading] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();

  // Theme handling based on localStorage
  useEffect(() => {
    const savedTheme = sessionStorage.getItem("admin-theme");
    const shouldBeDark = savedTheme === "dark";
    setIsDarkMode(shouldBeDark);
    
    // Apply theme to document for global CSS support
    if (shouldBeDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, []);

  // Listen for theme changes from other components
  useEffect(() => {
    const handleThemeChange = () => {
      const savedTheme = sessionStorage.getItem("admin-theme");
      const shouldBeDark = savedTheme === "dark";
      setIsDarkMode(shouldBeDark);
      
      // Apply theme to document
      if (shouldBeDark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    };

    window.addEventListener("themeChanged", handleThemeChange);
    
    // Listen for storage changes (other tabs)
    const handleStorageChange = (e) => {
      if (e.key === "admin-theme") {
        handleThemeChange();
      }
    };
    
    window.addEventListener("storage", handleStorageChange);

    // Listen for custom theme toggle events
    const handleCustomThemeChange = () => {
      handleThemeChange();
    };
    
    window.addEventListener("adminThemeToggled", handleCustomThemeChange);
    
    return () => {
      window.removeEventListener("themeChanged", handleThemeChange);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("adminThemeToggled", handleCustomThemeChange);
    };
  }, []);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); 

    if (formData.mobile_number.length !== 10) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Mobile Number',
        text: 'Mobile number must be exactly 10 digits.',
        background: isDarkMode ? '#1f2937' : '#fff',
        color: isDarkMode ? '#f9fafb' : '#000',
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(SummaryApi.AdminStudentCreate.url, {
        method: SummaryApi.AdminStudentCreate.method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...formData, is_active: '1' , is_self_registered: '1'})
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Student created successfully!',
          background: isDarkMode ? '#1f2937' : '#fff',
          color: isDarkMode ? '#f9fafb' : '#000',
        }).then(() => {
          navigate('/admin-panel/student-list');
        });
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          mobile_number: '',
          address: '',
          city: '',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to create student. Please try again.',
          background: isDarkMode ? '#1f2937' : '#fff',
          color: isDarkMode ? '#f9fafb' : '#000',
        });
      }
    } catch (error) {
      console.error("Submission error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An unexpected error occurred.',
        background: isDarkMode ? '#1f2937' : '#fff',
        color: isDarkMode ? '#f9fafb' : '#000',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900' : 'bg-gray-100'
    }`}>
      <div className={`w-full mx-4 sm:mx-6 md:max-w-4xl p-4 sm:p-6 md:p-10 shadow-md rounded-lg transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
      }`}>
        <h2 className={`text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center gap-2 ${
          isDarkMode ? 'text-white-400' : 'text-indigo-600'
        }`}>
          <FaUserPlus className="text-lg sm:text-xl" /> Create Student (Admin)
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Row 1: First + Last Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* First Name */}
            <div>
              <label className={`block font-medium text-sm sm:text-base ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>First Name *</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                className={`mt-1 block w-full px-3 sm:px-4 py-3 sm:py-3 text-sm sm:text-base rounded-md focus:ring transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border border-gray-300 focus:ring-indigo-200 focus:border-indigo-500'
                }`}
              />
            </div>

            {/* Last Name */}
            <div>
              <label className={`block font-medium text-sm sm:text-base ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Last Name *</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                className={`mt-1 block w-full px-3 sm:px-4 py-3 sm:py-3 text-sm sm:text-base rounded-md focus:ring transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border border-gray-300 focus:ring-indigo-200 focus:border-indigo-500'
                }`}
              />
            </div>
          </div>

          {/* Row 2: Email + Mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Email */}
            <div>
              <label className={`block font-medium text-sm sm:text-base ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`mt-1 block w-full px-3 sm:px-4 py-3 sm:py-3 text-sm sm:text-base rounded-md focus:ring transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border border-gray-300 focus:ring-indigo-200 focus:border-indigo-500'
                }`}
              />
            </div>

            {/* Mobile Number */}
            <div>
              <label className={`block font-medium text-sm sm:text-base ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Mobile Number *</label>
              <input
                type="tel"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d{0,10}$/.test(val)) {
                    setFormData((prev) => ({ ...prev, mobile_number: val }));
                  }
                }}
                required
                maxLength={10}
                inputMode="numeric"
                title="Please enter exactly 10 digits"
                className={`mt-1 block w-full px-3 sm:px-4 py-3 sm:py-3 text-sm sm:text-base rounded-md focus:ring transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border border-gray-300 focus:ring-indigo-200 focus:border-indigo-500'
                }`}
              />
            </div>
          </div>

          {/* Row 3: Address + City */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Address */}
            <div>
              <label className={`block font-medium text-sm sm:text-base ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className={`mt-1 block w-full px-3 sm:px-4 py-3 sm:py-3 text-sm sm:text-base rounded-md focus:ring transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border border-gray-300 focus:ring-indigo-200 focus:border-indigo-500'
                }`}
              />
            </div>

            {/* City */}
            <div>
              <label className={`block font-medium text-sm sm:text-base ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={`mt-1 block w-full px-3 sm:px-4 py-3 sm:py-3 text-sm sm:text-base rounded-md focus:ring transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' 
                    : 'border border-gray-300 focus:ring-indigo-200 focus:border-indigo-500'
                }`}
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full flex items-center justify-center font-semibold py-3 sm:py-3 px-4 text-sm sm:text-base rounded transition duration-200 ${
              loading
                ? 'bg-[#1C3664] text-white cursor-not-allowed'
                : isDarkMode
                  ? 'bg-[#1C3664] hover:bg-[#1A73E8] text-white'
                  : 'bg-[#1C3664] hover:bg-[#1A73E8] text-white'
            }`}
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Submitting...
              </>
            ) : (
              'Submit'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default StudentCreateAdmin;